import React, { forwardRef } from 'react';
import styled from 'styled-components';

type KanbanItemCardProps = {
  /** Card title (single line, truncated — same as the step card). */
  title: string;

  /** Leading element, rendered to the left of the content (e.g. status selector). */
  leading?: React.ReactNode;

  /** Trailing element on the title row (e.g. assignee avatar). */
  trailing?: React.ReactNode;

  /** Footer row content (e.g. date + actions). Omitted row when not provided. */
  footer?: React.ReactNode;

  /** Title-cases each word — kept on for steps; off for free-text task titles. */
  capitalizeTitle?: boolean;

  status?: string;
  isDone?: boolean;
  isDragging?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
} & Omit<React.ComponentPropsWithoutRef<'div'>, 'title' | 'onClick'>;

/**
 * Presentational kanban card shell shared by the project-overview step card
 * (`StepItem`) and the tasks board card (`TaskCard`). Owns the layout and
 * styling only — callers provide the status/assignee/footer content.
 */
export const KanbanItemCard = forwardRef<HTMLDivElement, KanbanItemCardProps>(
  (
    {
      title,
      leading,
      trailing,
      footer,
      capitalizeTitle,
      status,
      isDone,
      isDragging,
      onClick,
      ...rest
    },
    ref,
  ) => (
    <Container
      ref={ref}
      data-status={status}
      data-is-done={isDone}
      data-dragging={isDragging}
      onClick={onClick}
      {...rest}
    >
      {leading}

      <Wrapper>
        <Header className="row">
          <Title data-capitalize={capitalizeTitle}>{title}</Title>

          {trailing}
        </Header>

        {footer && <Footer className="row">{footer}</Footer>}
      </Wrapper>
    </Container>
  ),
);

const Wrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const Header = styled.div`
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
`;

const Footer = styled.div`
  gap: 0.4rem;
`;

const Container = styled.div`
  width: 100%;
  font-weight: 600;
  border-radius: var(--card-border-radius);
  background-color: var(--color-surface-tertiary);
  display: flex;
  padding: 0.8rem;
  position: relative;
  cursor: pointer;
  color: var(--step-title-color);
  user-select: none;

  &:not(:last-child) {
    margin-bottom: 0.8rem;
  }

  &[data-dragging='true'] {
    opacity: 0.5;
  }

  &[data-is-done='true'] {
    opacity: 0.6;
    background-color: var(--color-surface-secondary);
  }
`;

const Title = styled.div`
  font-size: 1.3rem;
  font-weight: 300;
  line-height: 1.6rem;
  margin-bottom: 1.2rem;
  margin-top: 0.4rem;
  min-width: 0;
  max-width: 87%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &[data-capitalize='true'] {
    text-transform: capitalize;
  }
`;
